import { ui } from '@/constants';
import { 
  IngredientGroupItemMetaData, 
  MonoMenuEntree, 
  Recipe, 
  MonoMenuTileItem, 
  IngredientMetadata, 
  IngredientGroupMetadata, 
  RecipeGroup, 
  INGREDIENTS_METADATA_COUNTRIES } from '@/models';
import Vue from 'vue';
import VueHorizontal from 'vue-horizontal';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgRecipesBanner',
  components: {
    VueHorizontal
  }
})
export default class CmgRecipesBanner extends Vue {
  currentIndex = 0;
  titleWidths: number[] = [];
  showedExtra = false;
  mobileTitle = '';
  mobileWidthRanges: MobileWidthRange[] = [];
  halfScreenWidth = window.screen.width / 2;
  cssTransition = true;

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.tablet;
  }

  get expanded(): boolean {
    return this.$appStore.state.ui.ingredientsPage.isRecipeBannerExpanded;
  }

  get selectedDistanceLeft(): number {
    return this.$appStore.state.ui.ingredientsPage.selectedRecipeDistance;
  }

  get isRecipeVisible(): boolean {
    return this.$appStore.state.ui.ingredientsPage.isSelectedRecipeVisible;
  }

  get titleText(): string {
    return this.expanded ? 'Back to all 53 Real Ingredients that make our recipes' : 'See how these 53 Real Ingredients make our recipes'
  }

  get iconText(): string {
    return this.expanded ? 'x' : '+'
  }

  get ingredientsMetadata(): IngredientMetadata[] {
    return this.$appStore.state.menu.ingredientsMetadata[INGREDIENTS_METADATA_COUNTRIES.US]?.data?.ingredients || [];
  }
  
  get ingredientGroupsMetadata(): IngredientGroupMetadata[] {
    return this.$appStore.state.menu.ingredientsMetadata[INGREDIENTS_METADATA_COUNTRIES.US]?.data?.ingredientGroups || [];
  }

  get recipeGroups(): RecipeGroup[] {
    return this.$appStore.state.monoMenu.recipeGroups;
  }

  get recipeIsSelected(): boolean {
    return !!this.selectedRecipe;
  }

  get selectedRecipe(): Recipe | null {
    return this.$appStore.state.ui.ingredientsPage.selectedRecipe;
  }

  get showSelectedPointer() {
    return this.isRecipeVisible && !this.isMobile;
  }

  async mounted() {
    await this.$services.monoMenu.awaitCacheItemsAndBuildMenu();

    if (this.isMobile) {
      this.mobileTitle = this.recipeGroups[0].title;
      this.calculateMobileRanges();
    }
  }

  onScroll(data: any) {
    this.setPointerVisibility();

    if (this.isMobile) {
      const halfScreenWidth = window.screen.width / 2;
      const updatedGroup = this.mobileWidthRanges.find(r => data.left > (r.left - halfScreenWidth) && data.left < (r.right - halfScreenWidth));
      if (updatedGroup && updatedGroup.title != this.mobileTitle) {
        this.mobileTitle = updatedGroup.title;
        this.cssTransition = !this.cssTransition;
        
        setTimeout(() => {
          this.cssTransition = !this.cssTransition;
        }, 250)
      }
    }
  }

  onClickExpand() {
    this.$services.ui.setRecipeBannerExpanded(!this.expanded);

    if (!this.expanded) {
      this.$services.ui.setRecipe(null);
      this.$services.ui.setIngredients(this.$appStore.state.menu.ingredientsMetadata[INGREDIENTS_METADATA_COUNTRIES.US]?.data?.ingredients || []);
    } else {
      this.calculateTitleWidths();
    }
  }

  isRecipeSelected(item: Recipe) {
    return item.itemId == this.$appStore.state.ui.ingredientsPage.selectedRecipe?.itemId;
  }

  selectRecipe(recipe: Recipe) {
    const recipeIngredients = this.$appStore.state.menu.ingredientsMetadata[INGREDIENTS_METADATA_COUNTRIES.US]?.data?.ingredients.filter(x => recipe.ingredients.includes(x.key)) || [];
    
    this.$services.ui.setRecipe(recipe);
    this.$services.ui.setIngredients(recipeIngredients);
    this.setPointerVisibility();
  }

  getDisplayName(item:  [MonoMenuTileItem | undefined, IngredientGroupItemMetaData]): string {
      return (item[0] as MonoMenuEntree).primaryFillingName ?? item[0]?.itemName;
  }

  setPointerVisibility() {
    if (this.selectedRecipe) {
      const selectedElements = this.$refs[this.selectedRecipe.itemId] as HTMLElement[];
      const groupsContainer = this.$refs.groupsContainer as HTMLElement;

      if (selectedElements && selectedElements[0]) {
        this.$services.ui.setSelectedRecipeDistance(selectedElements[0].getBoundingClientRect().left - groupsContainer.getBoundingClientRect().left + 25);
        this.$services.ui.setSelectedRecipeVisible(selectedElements[0].getBoundingClientRect().left > groupsContainer.getBoundingClientRect().left && selectedElements[0].getBoundingClientRect().left < groupsContainer.getBoundingClientRect().right);
      }
    }
  }

  calculateTitleWidths() {
    for(const group of this.recipeGroups) {
      this.titleWidths.push(this.calculateGroupWidth(group, false));
    }
  }

  calculateGroupWidth(group: RecipeGroup, isMobile: boolean) {
    return isMobile ? group.recipes.length * 110 + 40 : group.recipes.length * 138 + 20
  }

  calculateMobileRanges() {
    this.recipeGroups.forEach((group, index) => {
      let prevRight = index === 0 ? 0 : this.mobileWidthRanges[index - 1].right;
      this.mobileWidthRanges.push({
        title: group.title,
        left: prevRight,
        right: prevRight + this.calculateGroupWidth(group, true)
      });
    });
  }

  clickPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      (this.$refs.titleHorizontal as any).scrollToIndex(this.currentIndex);
      (this.$refs.groupHorizontal as any).scrollToIndex(this.currentIndex);
      this.setPointerVisibility();   
    }
  }

  clickNext() {
    if (this.currentIndex < this.recipeGroups.length - 1) {
      const currentGroupWidth = this.recipeGroups[this.currentIndex].recipes.length * 138 + 80;
      if (currentGroupWidth > 1275 && !this.showedExtra) {
        const groupHorizontal = this.$refs.groupHorizontal as any;
        // Figure out the math behind this hack
        (groupHorizontal.$el as Element).children[0].scrollTo({ left: 1600, behavior: 'smooth'});
        this.showedExtra = true;
      } else {
        this.currentIndex++;
        (this.$refs.titleHorizontal as any).scrollToIndex(this.currentIndex);
        (this.$refs.groupHorizontal as any).scrollToIndex(this.currentIndex);
        this.showedExtra = false;
      }

      this.setPointerVisibility();
    }
  }
}

export interface MobileWidthRange {
  title: string;
  left: number;
  right: number;
}